import EmptyList from '@/components/EmptyList';
import Filters, { orderByFilterEnum } from '@/components/Filter';
import Loader from '@/components/Loader';
import Tab from '@/components/Tab';
import Constants from '@/Constants';
import { useGetPaginated } from '@/hooks/useGetPaginated';
import { Program } from '@/models/Program';
import { ProgramRequest } from '@/models/ProgramRequest';
import { ApplicationState } from '@/store';
import { programsClear } from '@/store/program/action';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import ProgramCard from './ProgramCard';
import './style.scss';

const getQueryParams = ({
    applicationId,
    organizationId,
    moduleId,
    onlyHasProgramClass,
    isActive,
    status,
    courseId,
    withClassAmount,
    withCourseAmount,
    sumWorkload,
    name,
    locale,
    minWorkload,
    maxWorkload,
    orderBy,
    withWorkshopAmount,
}: ProgramRequest) => {
    let queryParams: string = `?applicationId=${applicationId}&organizationId=${organizationId}&moduleId=${moduleId}`;

    if (onlyHasProgramClass !== undefined) {
        queryParams += `&onlyHasProgramClass=${onlyHasProgramClass}`;
    }
    if (isActive !== undefined) {
        queryParams += `&isActive=${isActive}`;
    }
    if (status !== undefined) {
        queryParams += `&status=${status}`;
    }
    if (courseId !== undefined) {
        queryParams += `&courseId=${courseId}`;
    }
    if (withCourseAmount) {
        queryParams += `&withCourseAmount=${withCourseAmount}`;
    }
    if (sumWorkload) {
        queryParams += `&sumWorkload=${sumWorkload}`;
    }
    if (name !== undefined && name.length > 0) {
        queryParams += `&name=${name}`;
    }
    if (locale !== undefined) {
        queryParams += `&locale=${locale}`;
    }
    if (orderBy !== undefined) {
        queryParams += `&orderBy=${orderBy}`;
    }
    if (minWorkload !== undefined) {
        queryParams += `&minWorkload=${minWorkload}`;
    }
    if (maxWorkload !== undefined) {
        queryParams += `&maxWorkload=${maxWorkload}`;
    }
    if (withClassAmount !== undefined) {
        queryParams += `&withClassAmount=${withClassAmount}`;
    }
    if (withWorkshopAmount !== undefined) {
        queryParams += `&withWorkshopAmount=${withWorkshopAmount}`;
    }

    return queryParams;
};

const Programs: FC = () => {
    const { strings, identity } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        identity: state.identity.data,
    }));

    const [searchName, setSearchName] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);
    const [initialData, setInitialData] = useState(false);
    const [active, setActive] = useState(Constants.programStatus.PUBLISHED);

    // filters
    const [slideRange, setSlideRange] = useState<number[]>([0, 90]);
    const [fakeSlideRange, setFakeSlideRange] = useState<number[]>([0, 90]);
    const [orderBy, setOrderBy] = useState(orderByFilterEnum.CREATED_AT);
    const [mediaType, setMediaType] = useState({ VIDEO: false, AUDIO: false, HTML: false, EXTERNAL: false });
    const [language, setLanguage] = useState({ pt: false, en: false, '': false });
    const [visibility, setVisibility] = useState({ public: false, private: false });

    const history = useHistory();
    const match = useRouteMatch();
    const dispatch = useDispatch();

    const getKey = () => {
        const {
            user: { orgId, applicationId },
        } = identity;

        let locale: string | undefined = '';

        if (!language.pt && !language.en) locale = undefined;
        else if (language.pt && language.en) locale = undefined;
        else locale = language.pt ? Constants.language.PT : Constants.language.EN;

        const queryParams = getQueryParams({
            applicationId: applicationId,
            organizationId: orgId,
            moduleId: Constants.LMS_MODULE_ID,
            status: active,
            withCourseAmount: true,
            sumWorkload: true,
            name: searchName.trim(),
            minWorkload: slideRange[0],
            maxWorkload: slideRange[1] === 90 ? undefined : slideRange[1],
            locale: locale,
            orderBy: orderBy === orderByFilterEnum.ALPHABETICAL ? undefined : orderBy,
            withClassAmount: true,
            withWorkshopAmount: true,
        });

        return Constants.LMS_PROGRAMS_URI + queryParams;
    };

    const { data: programs, loading } = useGetPaginated<Program>(getKey, { revalidateOnMount: true });

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchName(e.target.value);
    };

    const handleNewProgram = (): void => {
        history.push(`${match.url}/`);
    };

    const renderEmpty = () => !loading && programs && programs.length <= 0;

    useEffect(() => {
        if (!loading) setInitialLoading(false);
        if (programs && programs.length > 0) setInitialData(true);
    }, [loading, programs]);

    useEffect(() => {
        dispatch(programsClear());
    }, [active]);

    return (
        <>
            {initialLoading && <Loader />}
            {!initialLoading && (
                <div className="lists">
                    <div className="list">
                        <div className="d-flex top-content">
                            <button className="btn btn-primary btn-sm new-item-button" onClick={handleNewProgram} type="button">
                                {strings.CREATE_NEW_PROGRAM}
                            </button>

                            <div className="form-group inner-addon left-addon">
                                <i className="material-icons-outlined align-middle input-icon">{Constants.icons.SEARCH}</i>
                                <input
                                    className="form-control"
                                    name="programSearch"
                                    onChange={handleSearch}
                                    placeholder={strings.SEARCH}
                                    type="search"
                                />
                            </div>
                        </div>

                        <Filters
                            entityType={Constants.entityTypes.COURSE}
                            fakeSlideRange={fakeSlideRange}
                            isProgram={true}
                            language={language}
                            mediaType={mediaType}
                            orderBy={orderBy}
                            setFakeSlideRange={setFakeSlideRange}
                            setLanguage={setLanguage}
                            setMediaType={setMediaType}
                            setOrderBy={setOrderBy}
                            setSlideRange={setSlideRange}
                            setVisibility={setVisibility}
                            slideRange={slideRange}
                            status={active}
                            visibility={visibility}
                        />

                        <div className="tabs-container">
                            <Tab
                                active={active === Constants.programStatus.PUBLISHED}
                                icon={Constants.icons.OUTLINED_FLAG}
                                setActive={() => setActive(Constants.programStatus.PUBLISHED)}
                                title={strings.PUBLISHED_PROGRAM}
                            />
                            <Tab
                                active={active === Constants.programStatus.DRAFT}
                                icon={Constants.icons.GESTURE}
                                setActive={() => setActive(Constants.programStatus.DRAFT)}
                                title={strings.DRAFT}
                            />
                            <Tab
                                active={active === Constants.programStatus.ARCHIVED}
                                icon={Constants.icons.HOW_TO_VOTE}
                                setActive={() => setActive(Constants.programStatus.ARCHIVED)}
                                title={strings.ARCHIVE_ITEMS}
                            />
                        </div>
                        <div className="list-container">
                            {loading ? (
                                <Loader />
                            ) : (
                                programs &&
                                (programs.length > 0 || searchName !== '' || initialData) && (
                                    <div className="list-lists">
                                        {programs && programs.map((p: Program, index: number) => <ProgramCard key={index} program={p} />)}
                                    </div>
                                )
                            )}
                            {renderEmpty() && (
                                <EmptyList
                                    buttonTitle={strings.CREATE_NEW_PROGRAM}
                                    handleNewList={handleNewProgram}
                                    icon={Constants.icons.BALLOT}
                                    title={strings.EMPTY_PROGRAM_LIST}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Programs;
